import { decodeHTML } from "entities";
import { observer } from "mobx-react";
import React from "react";

import { RichtextBlockModel } from "@courses-models/course";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { config } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/config";
import { colors } from "@apps/viewer/src/ts/config";

import {
  Preview,
  PreviewProps,
  PreviewState,
  PreviewTitle,
} from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/Preview";

/**
 * Renders a preview of richtext blocks consisting of title and an extract of
 * the text itself.
 */
@observer
class RichtextPreviewUnwrapped extends Preview<
  PreviewProps<RichtextBlockModel>
> {
  /**
   * Setup initial state
   */
  public state: PreviewState = {
    isActive: false,
  };

  /**
   * Renderer that'll inject the fallback icon, which is going to be used if
   * no thumbnail could be found for this block.
   */
  protected renderThumbnailFallbackIcon(): React.JSX.Element {
    return (
      <React.Fragment>
        <div className="text-wrapper">
          <p className="text">
            {decodeHTML(this.props.model.content.replace(/\<[^\>]*\>/g, " "))}
          </p>
        </div>

        <style jsx>{`
          .text-wrapper {
            /** Setup internal sizing */
            padding: 15px 25px;

            & .text {
              /** Allow up to 5 lines before cropping text */
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 5;
              overflow: hidden;

              /**
                             * On non-webkit browsers simply enforce max-height
                             * to crop to 5 lines.
                             */
              max-height: calc(5em * 1.5 - 0.25em);

              /** Since we use -webkit-box, then the white-space
                             * on top of the first line of text will remain, however
                             * there will be no whitespace on the bottom of the text block.
                             * Therefore we apply this manually to achieve equal
                             * borders.
                             */
              padding-bottom: 0.25em;

              /** Override defaults */
              margin: 0;

              /** Setup font */
              color: ${colors.darkGrey};
              line-height: 1.5;
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * There are no SubTitles available for RichText previews, simply return
   * title.
   */
  protected getTitle(): PreviewTitle {
    return {
      title: this.props.model.title,
    };
  }

  /**
   * MOCK - We need to implement a fetchContent, but there's really no other data
   * needed in order to render the preview for this block...
   */
  protected async fetchContent(): Promise<void> {
    return;
  }

  /**
   * MOCK - We need to implement a getDataKey, but there's really no use of it
   * for the RichtextPreview...
   */
  protected getDataKey(): string | undefined {
    return undefined;
  }

  /**
   * MOCK - We need to implement a getThumbnailUrl, but there's really no way
   * to get one for text pages...
   */
  protected async getThumbnailUrl(
    videoId: string | undefined,
  ): Promise<string | undefined> {
    return undefined;
  }
}

export const RichtextPreview = RichtextPreviewUnwrapped;
